/* ==========================================================================
   FILE: global/context/AppContext.jsx
   IAMONEAI - Main Application Context Provider
   Manages user state, authentication, active context, and global app state
   ========================================================================== */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { STORAGE_KEYS, DEFAULT_CONTEXT, CONTEXTS, MESSAGES } from '../config/constants';
import { devLog, devError } from '../config/environment';

// ==========================================================================
// CONTEXT CREATION
// ==========================================================================

const AppContext = createContext(null);

// ==========================================================================
// INITIAL STATE
// ==========================================================================

const initialState = {
  // Authentication
  isAuthenticated: false,
  isLoading: true,
  
  // User data
  user: null,
  iin: null,
  
  // Brain data (from API)
  brain: null,
  
  // Active memory context
  activeContext: DEFAULT_CONTEXT,
  
  // Memories (cached)
  memories: [],
  
  // Goals (cached)
  goals: [],
  
  // UI State
  isChatLoading: false,
  error: null,
};

// ==========================================================================
// PROVIDER COMPONENT
// ==========================================================================

export function AppProvider({ children }) {
  // ----- Core State -----
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [iin, setIin] = useState(null);
  const [brain, setBrain] = useState(null);
  
  // ----- Context & Memory State -----
  const [activeContext, setActiveContextState] = useState(DEFAULT_CONTEXT);
  const [memories, setMemories] = useState([]);
  const [goals, setGoals] = useState([]);
  
  // ----- UI State -----
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [error, setError] = useState(null);

  // ==========================================================================
  // LOCAL STORAGE HELPERS
  // ==========================================================================

  const getStoredValue = useCallback((key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      devError('Failed to read from localStorage:', e);
      return null;
    }
  }, []);

  const setStoredValue = useCallback((key, value) => {
    try {
      if (value === null) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, value);
      }
    } catch (e) {
      devError('Failed to write to localStorage:', e);
    }
  }, []);

  // ==========================================================================
  // AUTHENTICATION
  // ==========================================================================

  /**
   * Log in user with IIN and pre-fetched data
   * @param {string} userIin - The user's IIN
   * @param {Object} userData - User identity data (optional)
   * @param {Object} brainData - Brain data from API (optional)
   */
  const login = useCallback((userIin, userData = null, brainData = null) => {
    devLog('Logging in user:', userIin);
    
    // Set core state
    setIin(userIin);
    setIsAuthenticated(true);
    setError(null);
    
    // Set user data
    if (userData) {
      setUser(userData);
    } else {
      setUser({ iin: userIin });
    }
    
    // Set brain data
    if (brainData) {
      setBrain(brainData);
      
      // Set active context from brain if available
      if (brainData.activeContext) {
        setActiveContextState(brainData.activeContext);
        setStoredValue(STORAGE_KEYS.CONTEXT, brainData.activeContext);
      }
    }
    
    // Persist to storage
    setStoredValue(STORAGE_KEYS.IIN, userIin);
    setStoredValue(STORAGE_KEYS.LAST_ACTIVE, new Date().toISOString());
    
    // Store user data for session restore
    if (userData || brainData) {
      try {
        localStorage.setItem('iamoneai_user', JSON.stringify({
          iin: userIin,
          user: userData,
          brain: brainData,
        }));
      } catch (e) {
        devError('Failed to store user data:', e);
      }
    }
    
    devLog('Login complete. Brain:', brainData ? 'loaded' : 'not loaded');
  }, [setStoredValue]);

  /**
   * Log out user and clear all data
   */
  const logout = useCallback(() => {
    devLog('Logging out user');
    
    setIsAuthenticated(false);
    setUser(null);
    setIin(null);
    setBrain(null);
    setMemories([]);
    setGoals([]);
    setActiveContextState(DEFAULT_CONTEXT);
    setError(null);
    
    // Clear storage
    setStoredValue(STORAGE_KEYS.IIN, null);
    setStoredValue(STORAGE_KEYS.TOKEN, null);
    setStoredValue(STORAGE_KEYS.CONTEXT, null);
    
    try {
      localStorage.removeItem('iamoneai_user');
      localStorage.removeItem('iamoneai_iin');
    } catch (e) {
      devError('Failed to clear storage:', e);
    }
  }, [setStoredValue]);

  /**
   * Check for existing session on mount
   */
  const checkAuth = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const storedIin = getStoredValue(STORAGE_KEYS.IIN);
      const storedContext = getStoredValue(STORAGE_KEYS.CONTEXT);
      
      if (storedIin) {
        devLog('Found stored IIN:', storedIin);
        setIin(storedIin);
        setIsAuthenticated(true);
        
        // Try to restore user data
        try {
          const storedUserData = localStorage.getItem('iamoneai_user');
          if (storedUserData) {
            const parsed = JSON.parse(storedUserData);
            if (parsed.user) setUser(parsed.user);
            if (parsed.brain) setBrain(parsed.brain);
            devLog('Restored user session with brain:', !!parsed.brain);
          }
        } catch (e) {
          devError('Failed to restore user data:', e);
        }
        
        if (storedContext && Object.values(CONTEXTS).includes(storedContext)) {
          setActiveContextState(storedContext);
        }
      }
    } catch (e) {
      devError('Auth check failed:', e);
    } finally {
      setIsLoading(false);
    }
  }, [getStoredValue]);

  // ==========================================================================
  // CONTEXT SWITCHING
  // ==========================================================================

  /**
   * Switch active memory context
   */
  const setActiveContext = useCallback((newContext) => {
    if (!Object.values(CONTEXTS).includes(newContext)) {
      devError('Invalid context:', newContext);
      return;
    }
    
    devLog('Switching context to:', newContext);
    setActiveContextState(newContext);
    setStoredValue(STORAGE_KEYS.CONTEXT, newContext);
  }, [setStoredValue]);

  // ==========================================================================
  // DATA UPDATERS
  // ==========================================================================

  /**
   * Update brain data (after API fetch or changes)
   */
  const updateBrain = useCallback((brainData) => {
    setBrain(brainData);
    
    // Sync active context if different
    if (brainData?.activeContext && brainData.activeContext !== activeContext) {
      setActiveContextState(brainData.activeContext);
      setStoredValue(STORAGE_KEYS.CONTEXT, brainData.activeContext);
    }
    
    // Update stored session
    try {
      const storedUserData = localStorage.getItem('iamoneai_user');
      if (storedUserData) {
        const parsed = JSON.parse(storedUserData);
        parsed.brain = brainData;
        localStorage.setItem('iamoneai_user', JSON.stringify(parsed));
      }
    } catch (e) {
      devError('Failed to update stored brain:', e);
    }
  }, [activeContext, setStoredValue]);

  /**
   * Update user data
   */
  const updateUser = useCallback((userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  }, []);

  /**
   * Update memories cache
   */
  const updateMemories = useCallback((newMemories) => {
    setMemories(newMemories);
  }, []);

  /**
   * Add a single memory to cache
   */
  const addMemory = useCallback((memory) => {
    setMemories(prev => [memory, ...prev]);
  }, []);

  /**
   * Update goals cache
   */
  const updateGoals = useCallback((newGoals) => {
    setGoals(newGoals);
  }, []);

  /**
   * Add a single goal to cache
   */
  const addGoal = useCallback((goal) => {
    setGoals(prev => [goal, ...prev]);
  }, []);

  /**
   * Update a goal's status
   */
  const updateGoalStatus = useCallback((goalId, newStatus) => {
    setGoals(prev => prev.map(g => 
      g.id === goalId ? { ...g, status: newStatus } : g
    ));
  }, []);

  // ==========================================================================
  // ERROR HANDLING
  // ==========================================================================

  /**
   * Set global error
   */
  const setGlobalError = useCallback((errorMessage) => {
    setError(errorMessage);
  }, []);

  /**
   * Clear global error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ==========================================================================
  // DERIVED STATE
  // ==========================================================================

  const activeContextConfig = useMemo(() => {
    const contexts = brain?.contexts || {};
    return contexts[activeContext] || {
      name: activeContext.charAt(0).toUpperCase() + activeContext.slice(1),
      icon: '🏠',
      tone: 'friendly',
      style: 'casual',
    };
  }, [brain, activeContext]);

  const userName = useMemo(() => {
    return user?.firstName || 
           user?.owner ||
           brain?.metadata?.owner || 
           brain?.identityKernel?.name || 
           null;
  }, [user, brain]);

  const activeGoals = useMemo(() => {
    return goals.filter(g => g.status === 'pending' || g.status === 'overdue');
  }, [goals]);

  const completedGoals = useMemo(() => {
    return goals.filter(g => g.status === 'done');
  }, [goals]);

  // ==========================================================================
  // CONTEXT VALUE
  // ==========================================================================

  const value = useMemo(() => ({
    // Auth state
    isAuthenticated,
    isLoading,
    
    // User data
    user,
    iin,
    userName,
    
    // Brain
    brain,
    updateBrain,
    
    // Context
    activeContext,
    activeContextConfig,
    setActiveContext,
    
    // Memories
    memories,
    updateMemories,
    addMemory,
    
    // Goals
    goals,
    activeGoals,
    completedGoals,
    updateGoals,
    addGoal,
    updateGoalStatus,
    
    // Auth actions
    login,
    logout,
    updateUser,
    
    // UI state
    isChatLoading,
    setIsChatLoading,
    
    // Error handling
    error,
    setGlobalError,
    clearError,
  }), [
    isAuthenticated,
    isLoading,
    user,
    iin,
    userName,
    brain,
    updateBrain,
    activeContext,
    activeContextConfig,
    setActiveContext,
    memories,
    updateMemories,
    addMemory,
    goals,
    activeGoals,
    completedGoals,
    updateGoals,
    addGoal,
    updateGoalStatus,
    login,
    logout,
    updateUser,
    isChatLoading,
    error,
    setGlobalError,
    clearError,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

// ==========================================================================
// HOOK
// ==========================================================================

/**
 * Hook to access app context
 * @returns {Object} App context value
 * @throws {Error} If used outside AppProvider
 */
export function useApp() {
  const context = useContext(AppContext);
  
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  
  return context;
}

// ==========================================================================
// CONVENIENCE HOOKS
// ==========================================================================

/**
 * Hook for auth-related state only
 */
export function useAuth() {
  const { isAuthenticated, isLoading, user, iin, login, logout } = useApp();
  return { isAuthenticated, isLoading, user, iin, login, logout };
}

/**
 * Hook for context-related state only
 */
export function useActiveContext() {
  const { activeContext, activeContextConfig, setActiveContext } = useApp();
  return { activeContext, activeContextConfig, setActiveContext };
}

/**
 * Hook for brain data only
 */
export function useBrainContext() {
  const { brain, updateBrain } = useApp();
  return { brain, updateBrain };
}

/**
 * Hook for goals only
 */
export function useGoals() {
  const { goals, activeGoals, completedGoals, updateGoals, addGoal, updateGoalStatus } = useApp();
  return { goals, activeGoals, completedGoals, updateGoals, addGoal, updateGoalStatus };
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export { AppContext };
export default AppProvider;